import React, { useEffect, useMemo, useState } from "react";
import MonthNavigator from "./MonthNavigator";
import TransactionTable from "./TransactionTable";
import BalanceFooter from "./BalanceFooter";
import { useDispatch, useSelector } from "react-redux";
import {
  getBalanceHistory,
  shareBalanceSheet,
} from "../../redux/thunks/customerRecordThunk";
import toast from "react-hot-toast";
import Pagination from "../Pagination";
import BalanceTableShimmer from "../Preloader/BalanceTableShimmer";

const BalanceSheet = ({ customerId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const dispatch = useDispatch();
  const { pagination, isCustmerLoading } = useSelector(
    (state) => state.customerRecords,
  );

  const { current_page, total_pages, total_records } = pagination;
  const [ledger, setLedger] = useState([]);
  const [closingBalance, setClosingBalance] = useState("0.00");
  const [currency, setCurrency] = useState("$");
  const [page, setPage] = useState(1);
  const getMonthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);

    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);

    const format = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`;

    return {
      start_date: format(start),
      end_date: format(end), // 🔥 IMPORTANT
    };
  };

  useEffect(() => {
    const { start_date, end_date } = getMonthRange(currentDate);

    dispatch(
      getBalanceHistory({
        service_provider_customer_id: customerId,
        start_date,
        end_date,
        page: page,
      }),
    )
      .unwrap()
      .then((res) => {
        const data = res.data;
        setLedger(data.ledger || []);
        setClosingBalance(data.closing_balance);
        setCurrency(data.currency_symbol);
      });
  }, [dispatch, customerId, currentDate, page]);

  const transactions = useMemo(() => {
    return ledger.map((item, index) => {
      const amount =
        item.balance_type === "debit"
          ? -Number(item.balance_change.replace(/,/g, ""))
          : Number(item.balance_change.replace(/,/g, ""));

      return {
        id: item.id || index,
        type: item.balance_type === "credit" ? "credit" : "debit",
        date: item.date.split("/").reverse().join("-"), // DD/MM/YYYY → YYYY-MM-DD
        title: item.transaction,
        refId: item.reference_id,
        amount,

        // 🔥 highlight rules
        highlight:
          item.payment_activity_type === "monthly_balance_carry_forward" ||
          amount > 0, // 🟢 green / positive balance
      };
    });
  }, [ledger]);

  const onShareSheet = () => {
    const { start_date, end_date } = getMonthRange(currentDate);

    dispatch(
      shareBalanceSheet({
        service_provider_customer_id: customerId,
        start_date,
        end_date,
      }),
    )
      .unwrap()
      .then((res) => {
        const { file_url, file_name } = res.data;

        // 🔽 download trigger
        const link = document.createElement("a");
        link.href = file_url;
        link.download = file_name || "balance_sheet.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (res.success) {
          toast.success(res.message || "Downloaded successfully");
        }
      });
  };

  return (
    <>
      <div className="max-w-full mx-auto md:bg-white md:rounded-xl md:shadow p-1 md:p-4 space-y-4">
        {/* 👇 MONTH SWITCHER */}
        <MonthNavigator date={currentDate} onChange={setCurrentDate} />

        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-sm">Balance Sheet</h2>
          <button
            className="border border-[#FF6943] text-[#FF6943] px-3 py-1 rounded-full text-xs cursor-pointer"
            onClick={onShareSheet}
          >
            Export Balance Sheet
          </button>
        </div>

        {/* 👇 TABLE */}
        {isCustmerLoading ? (
          <BalanceTableShimmer />
        ) : (
          <TransactionTable transactions={transactions} />
        )}

        {/* 👇 FOOTER BALANCE */}
        <BalanceFooter amount={`${currency} ${closingBalance}`} />
      </div>
      <div>
        <Pagination
          current={current_page}
          total={total_pages}
          onPageChange={setPage}
        />
      </div>
    </>
  );
};

export default BalanceSheet;
